/**
 * Exclusion detection (DRILL-01/02, D-01/D-02/D-03/D-04).
 *
 * `stampEffectiveExcluded` resolves a single `effectiveExcluded` boolean onto
 * every lap, called ONCE from `deriveWorkout` right after `buildLaps` (D-03).
 * The five aggregate consumers then `.filter(l => !l.effectiveExcluded)`; never
 * pass an `excludeSet` into five functions (the documented missed-call-site trap).
 *
 * REST-GAP PASS REMOVED (TW8): the original Pass 1 excluded any length whose
 * preceding inter-lap gap was >= REST_GAP_MS. That was the bug. A gap BEFORE a
 * length is just rest (a wall touch, a conversation break); the length that
 * FOLLOWS is real swimming. On the 06-08 ground-truth swim that pass dropped the
 * first real length of sets 2/6/7/8/9 (laps 7/53/65/81 — normal SWOLF, none a
 * glitch). Removed per TW8 decision 1: count those lengths. Genuine watch
 * glitches are caught independently by the phantom fingerprint (pass 2.5), which
 * keys off sub-13s duration + under-strokes, NOT off the rest gap.
 *
 * Surviving passes, in order:
 *   2. 2SD OUTLIER (review-surfacing only, TW8b) — compute the mean + POPULATION
 *      SD of the lap SWOLF scores. A lap >2SD from the mean in EITHER direction
 *      (slow or fast) is surfaced as review-eligible so the deck can highlight it,
 *      but is NEVER auto-excluded. "Everything counts; never auto-exclude slow
 *      drills" — a slow length is real swimming (a drill, a breaststroke length,
 *      a recovery); a fast one is a sprint. This pass no longer touches
 *      effectiveExcluded at all.
 *   2.5. PHANTOM FINGERPRINT — split-length watch artifacts (duration < 13s AND
 *      few/no implied strokes) → auto-exclude + review-eligible (see constants).
 *      This is now the ONLY auto-exclusion pass.
 *   3. LABEL OVERRIDE (D-04) — a persisted LapLabel matching a lap by lapIndex
 *      sets effectiveExcluded = label.excludedFromAggregates, overriding the
 *      detection default in BOTH directions. Persisted label always wins.
 *
 * SD POPULATION NOTE: the SD is computed over ALL non-null-SWOLF laps. As of
 * TW8b the 2SD pass only surfaces outliers for review and never excludes, so the
 * slow-side threshold no longer removes any length from the aggregates — it just
 * flags which laps the review deck should highlight. Pitfall 4 (a monstrous-SWOLF
 * lap inflating SD) therefore only affects which laps get surfaced, not pace math.
 */
import type { Lap } from "./laps";
import type { LapLabel } from "@contract/types";
import { matchKeyToLaps } from "./correctionKey";

export interface ExclusionResult {
  /** Lap indices (1-based) that are >2SD outliers in EITHER direction (both
   *  kept-in as of TW8b — surfaced for review, never auto-excluded) OR
   *  phantom-fingerprinted (pass 2.5, the only auto-exclusion). The review
   *  side-channel Plan 03's deck reads to know which laps to surface as
   *  REVIEW-tier cards. Sorted ascending. */
  reviewLapIndices: number[];
  /** Lap indices (1-based) the phantom fingerprint pass excluded; subset of
   *  reviewLapIndices. Lets the deck and tests distinguish artifact-exclusions
   *  from rest/outlier ones. */
  phantomLapIndices: number[];
}

/**
 * Phantom fingerprint (pass 2.5): split-length watch artifact — one real length
 * recorded as two short events. Fingerprint = duration < 13s AND (implied
 * strokes <= 8 OR no HKSWOLFScore), where implied strokes = lap.swolf -
 * lap.durationSeconds. No real 25m length is under 13s with <= 8 strokes; a
 * true sprint has MORE strokes, not fewer. Evidence: docs/roadmap.md item 1
 * (June 11 late-start pair, 3/3 artifact laps match, watch A zero hits) + the
 * 2026-06-11 solo swim (4/4 match, incl. one with no SWOLF at all).
 * Second-swim revisit trigger fired.
 */
const PHANTOM_MAX_DURATION_S = 13;
const PHANTOM_MAX_STROKES = 8;

/**
 * Stamp `lap.effectiveExcluded` in place and return the review-eligibility
 * side-channel. Mutating (per D-03 single-stamp design) — returns metadata only.
 */
export function stampEffectiveExcluded(laps: Lap[], labels: LapLabel[]): ExclusionResult {
  const reviewLapIndices: number[] = [];
  const phantomLapIndices: number[] = [];

  // Pass 1 (REST/WALL) removed in TW8 — a gap before a length is rest; the
  // length that follows is real swimming. Genuine glitches are caught by the
  // phantom fingerprint (pass 2.5), not by the rest gap. See module doc.

  // --- Pass 2: 2SD OUTLIER (D-01 Smart-split). ---
  // Population = all laps carrying a SWOLF score. (See SD POPULATION NOTE above.)
  const swolfValues: number[] = [];
  for (const lap of laps) {
    if (lap.swolf !== null) swolfValues.push(lap.swolf);
  }
  // Guards: n<3 → SD meaningless; sd===0 → threshold collapses onto the mean.
  if (swolfValues.length >= 3) {
    const mean = swolfValues.reduce((s, v) => s + v, 0) / swolfValues.length;
    // POPULATION SD (divide by n, NOT n-1) — describing THIS workout's own laps.
    const variance = swolfValues.reduce((s, v) => s + (v - mean) ** 2, 0) / swolfValues.length;
    const sd = Math.sqrt(variance);
    if (sd > 0) {
      const slowThreshold = mean + 2 * sd;
      const fastThreshold = mean - 2 * sd;
      for (const lap of laps) {
        if (lap.swolf === null) continue;
        // TW8b: outliers in EITHER direction are SURFACED for review but NEVER
        // auto-excluded. A slow length is real swimming (a drill, a breaststroke
        // length, a recovery) — "everything counts; never auto-exclude slow
        // drills". A fast length is a sprint. Only the phantom pass (2.5) and a
        // persisted user label (3) may exclude. This also fixes the stroke-blind
        // bug where slow breaststroke read as an outlier on freestyle-heavy swims.
        if (lap.swolf > slowThreshold || lap.swolf < fastThreshold) {
          reviewLapIndices.push(lap.index);
        }
      }
    }
  }

  // --- Pass 2.5: PHANTOM FINGERPRINT (split-length artifact) ---
  // Order is load-bearing: the persisted-label pass (3) must still win in BOTH
  // directions over the fingerprint. The reviewLapIndices add is the override
  // path: the deck surfaces the lap, a keep-swipe writes a persisted label
  // (excludedFromAggregates=false) and pass 3 reinstates it. The null-SWOLF
  // branch matters: pass 2 cannot see those laps at all.
  for (const lap of laps) {
    if (lap.durationSeconds >= PHANTOM_MAX_DURATION_S) continue;
    const isPhantom =
      lap.swolf === null || lap.swolf - lap.durationSeconds <= PHANTOM_MAX_STROKES;
    if (isPhantom) {
      lap.effectiveExcluded = true;
      lap.exclusionReason = "Glitch — too short"; // jargon-free why (TW8)
      phantomLapIndices.push(lap.index);
      if (!reviewLapIndices.includes(lap.index)) reviewLapIndices.push(lap.index);
    }
  }

  // --- Pass 3: LABEL OVERRIDE (D-04) — persisted label wins, both directions. ---
  //
  // Matching strategy (T3b additive window):
  //   - If the label carries ALL THREE composite-key fields (offsetSeconds,
  //     durationSeconds, duplicateOrdinal non-null), resolve the target lap via
  //     matchKeyToLaps. If that returns null the label is an ORPHAN for this swim
  //     (present-but-unmatched key → skip; do NOT fall back to lapIndex).
  //   - If the label has no composite-key fields, fall back to lapIndex matching
  //     (legacy labels saved before T3a migration).
  if (labels.length > 0) {
    const lapKeys = laps.map((l) => l.correctionKey);
    // Legacy fallback: lapIndex → lap array index (0-based).
    const byIndex = new Map<number, number>(); // lapIndex → array position
    for (let i = 0; i < laps.length; i++) byIndex.set(laps[i].index, i);

    for (const label of labels) {
      let arrayIdx: number | null | undefined;

      if (
        label.offsetSeconds != null &&
        label.durationSeconds != null &&
        label.duplicateOrdinal != null
      ) {
        // Composite-key path: orphan (null) → skip entirely.
        arrayIdx = matchKeyToLaps(
          {
            offsetSeconds: label.offsetSeconds,
            durationSeconds: label.durationSeconds,
            duplicateOrdinal: label.duplicateOrdinal,
          },
          lapKeys,
        );
        if (arrayIdx === null) continue; // orphan — never misapply
      } else {
        // Legacy label: fall back to lapIndex.
        arrayIdx = byIndex.get(label.lapIndex);
        if (arrayIdx === undefined) continue;
      }

      const lap = laps[arrayIdx];
      lap.effectiveExcluded = label.excludedFromAggregates;
      if (!label.excludedFromAggregates) {
        // User re-included it: a counted length carries no reason (TW8 dec. 3).
        lap.exclusionReason = null;
      } else if (lap.exclusionReason === null) {
        // User marked it out with no prior auto-reason to preserve. Plain
        // sentence, not jargon (blind panel: "marked out" — out of WHAT? — was
        // the strongest single signal in a 5-panelist review, 2026-07-12).
        lap.exclusionReason = "Not counted in your totals";
      }
    }
  }

  // Deterministic deck order now that two passes feed the review side-channel.
  reviewLapIndices.sort((a, b) => a - b);

  return { reviewLapIndices, phantomLapIndices };
}
